import React from 'react'

import { useTranslation } from '@/shared/hooks/useIntractionTranslate'
import type { IndividualSessionFeedbackData } from '../../../../types/IndividualSessionFeedbackTypes'
import type { ProfileIndicator } from '../../../feedbackGenerator/components/profileIndicatorTypes'
import {
  generateProfileDescriptionFromStrengths,
  generateProfileTitleFromStrengths,
  mapImprovementIdeaToVisualization,
  transformTopStrengthsToIndicators
} from '../helpers/latestSessionFeedbackHelpers'

export type LatestSessionFeedbackView = {
  profileIndicators: ProfileIndicator[]
  profileTitle: string
  profileDescription: string
  improvementVisualization: { description: string; recommendation: string }
  sessionTimeFormatted: string
}

const EMPTY_IMPROVEMENT_VISUALIZATION = { description: '', recommendation: '' }

/**
 * Everything the feedback sections need that is derived rather than fetched. Shared by the full page and
 * the coach's modal, so there is one set of titles and indicators rather than two to keep in step.
 */
export function useLatestSessionFeedbackView(
  feedbackData: IndividualSessionFeedbackData | undefined
): LatestSessionFeedbackView {
  const { t } = useTranslation()

  const topStrengths = feedbackData?.topStrengths
  const improvementIdea = feedbackData?.improvementIdea
  const sessionDate = feedbackData?.sessionDate

  const profileIndicators = React.useMemo(
    () => (topStrengths ? transformTopStrengthsToIndicators(topStrengths, t) : []),
    [topStrengths, t]
  )

  const improvementVisualization = React.useMemo(
    () => (improvementIdea ? mapImprovementIdeaToVisualization(improvementIdea, t) : EMPTY_IMPROVEMENT_VISUALIZATION),
    [improvementIdea, t]
  )

  const profileTitle = React.useMemo(
    () => (topStrengths ? generateProfileTitleFromStrengths(topStrengths) : ''),
    [topStrengths]
  )

  const profileDescription = React.useMemo(
    () => (topStrengths ? generateProfileDescriptionFromStrengths(topStrengths, t) : ''),
    [topStrengths, t]
  )

  const sessionTimeFormatted = React.useMemo(() => {
    const fallback = t('gamer.feedback.sessionTime.default', 'Latest Session')
    if (!sessionDate) return fallback
    // A malformed stored date must not take the whole panel down with it.
    const parsed = new Date(sessionDate)
    if (Number.isNaN(parsed.getTime())) return fallback
    // Two games in a day (or on consecutive days) are only telling apart by the time of day.
    return parsed.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }, [sessionDate, t])

  return { profileIndicators, profileTitle, profileDescription, improvementVisualization, sessionTimeFormatted }
}
